// PointsOfInterestSystem.js
// Handles procedural placement of landmarks and structures on the island

import * as THREE from 'three';
import NoiseGenerator from './NoiseGenerator.js';
import BiomeManager from './BiomeManager.js';

/**
 * PointsOfInterestSystem - Manages landmark and structure placement for the island
 * Uses biome and height data to determine suitable locations
 */
class PointsOfInterestSystem {
  /**
   * Define landmark types and their properties
   */
  static LANDMARK_TYPES = {
    TOWER: {
      id: 'tower',
      name: 'Watchtower',
      scale: { min: 1.0, max: 1.5 },
      biomes: ['grassland', 'forest', 'mountains'],
      preferHigh: true,
      requireFlat: false,
      color: 0x8B4513, // Brown
      dimensions: { width: 2, height: 8, depth: 2 }
    },
    RUINS: {
      id: 'ruins',
      name: 'Ancient Ruins',
      scale: { min: 0.8, max: 1.2 },
      biomes: ['grassland', 'beach', 'rocky_terrain'],
      preferHigh: false,
      requireFlat: true,
      color: 0xA0A0A0, // Gray
      dimensions: { width: 5, height: 1.5, depth: 5 }
    },
    CABIN: {
      id: 'cabin',
      name: 'Cabin',
      scale: { min: 0.8, max: 1.0 },
      biomes: ['forest', 'grassland'],
      preferHigh: false,
      requireFlat: true,
      color: 0x8B4513, // Brown
      dimensions: { width: 3, height: 2.5, depth: 3 }
    },
    LIGHTHOUSE: {
      id: 'lighthouse',
      name: 'Lighthouse',
      scale: { min: 1.0, max: 1.2 },
      biomes: ['beach'],
      preferHigh: true,
      requireFlat: true,
      color: 0xFFFFFF, // White
      dimensions: { width: 2, height: 10, depth: 2 }
    },
    TEMPLE: {
      id: 'temple',
      name: 'Ancient Temple',
      scale: { min: 1.0, max: 1.5 },
      biomes: ['grassland', 'rocky_terrain'],
      preferHigh: true,
      requireFlat: true,
      color: 0xD2B48C, // Tan
      dimensions: { width: 6, height: 4, depth: 6 }
    }
  };

  /**
   * Constructor for the Points of Interest System
   * @param {Object} options - Configuration options
   * @param {string|number} options.seed - Seed for procedural generation
   * @param {number} options.minDistance - Minimum distance between POIs
   * @param {number} options.count - Target number of POIs to place
   * @param {boolean} options.debug - Whether to show debug visualizations
   */
  constructor(options = {}) {
    this.options = {
      seed: 'landmarks',
      minDistance: 30, // Minimum distance between landmarks in world units
      count: 5, // Target number of landmarks to place
      debug: false,
      ...options
    };
    
    // Collection of placed landmarks
    this.landmarks = [];
    
    // Object for storing custom model preloads
    this.modelCache = {};
    
    // Debug visualizations
    this.debugObjects = [];
  }

  /**
   * Place landmarks on the island based on terrain and biome data
   * @param {Array<Array<number>>} heightMap - 2D height map data
   * @param {Array<Array<Object>>} biomeMap - 2D biome map data
   * @param {Array<Array<number>>} islandMask - 2D island mask data
   * @param {THREE.Scene} scene - The Three.js scene
   * @param {Array<THREE.Vector3>} existingPositions - Positions to avoid (like spawn points)
   * @returns {Array<THREE.Object3D>} Array of placed landmarks
   */
  placeLandmarks(heightMap, biomeMap, islandMask, scene, existingPositions = []) {
    console.log('Placing landmarks with seed:', this.options.seed);
    
    // Clear any existing landmarks
    this.landmarks.forEach(landmark => {
      if (landmark.parent) {
        landmark.parent.remove(landmark);
      }
    });
    this.landmarks = [];
    
    // Find suitable locations for landmarks
    const locations = this.findLandmarkLocations(
      heightMap, biomeMap, islandMask, existingPositions
    );
    
    // Create and place landmarks at these locations
    locations.forEach(location => {
      const landmark = this.createLandmark(location.type, location.worldPos, scene);
      if (landmark) {
        this.landmarks.push(landmark);
      }
    });
    
    console.log(`Placed ${this.landmarks.length} landmarks`);
    return this.landmarks;
  }
  
  /**
   * Find suitable locations for landmarks based on terrain and biome
   * @param {Array<Array<number>>} heightMap - 2D height map data
   * @param {Array<Array<Object>>} biomeMap - 2D biome map data
   * @param {Array<Array<number>>} islandMask - 2D island mask data
   * @param {Array<THREE.Vector3>} existingPositions - Positions to avoid (like spawn points)
   * @returns {Array<Object>} Array of landmark locations with type and position
   */
  findLandmarkLocations(heightMap, biomeMap, islandMask, existingPositions = []) {
    const width = heightMap.length;
    const height = heightMap[0].length;
    const locations = [];
    
    // World size (30 units is a reasonable size for gameplay)
    const worldSize = 256;
    
    // Calculate the scaling factor from height map to world coordinates
    const scaleFactor = worldSize / width;
    
    // Minimum distance between landmarks (in height map units)
    const minDistanceHM = Math.floor(this.options.minDistance / scaleFactor);
    
    // Generate potential spots for each landmark type
    const landmarkTypes = Object.keys(PointsOfInterestSystem.LANDMARK_TYPES);
    
    // Shuffle landmark types for variety
    const shuffledTypes = this.shuffleArray([...landmarkTypes]);
    
    // Calculate center point
    const center = Math.floor(width / 2);
    
    // For each landmark type, try to find a suitable location
    for (let i = 0; i < shuffledTypes.length && locations.length < this.options.count; i++) {
      const landmarkType = shuffledTypes[i];
      const landmark = PointsOfInterestSystem.LANDMARK_TYPES[landmarkType];
      
      // Get a region to search based on the landmark's preferences
      const searchParams = this.getSearchRegion(landmark, width, height);
      
      // Find potential locations
      let potentialLocations = [];
      
      // Try multiple times to find a suitable spot
      for (let attempt = 0; attempt < 20 && potentialLocations.length === 0; attempt++) {
        const random = this.seededRandom(`${this.options.seed}-region-${landmarkType}-${attempt}`);
        const regionX = Math.floor(searchParams.centerX + (random() - 0.5) * searchParams.radiusX * 2);
        const regionY = Math.floor(searchParams.centerY + (random() - 0.5) * searchParams.radiusY * 2);
        
        // Define search region boundaries
        const minX = Math.max(0, regionX - searchParams.searchRadius);
        const maxX = Math.min(width - 1, regionX + searchParams.searchRadius);
        const minY = Math.max(0, regionY - searchParams.searchRadius);
        const maxY = Math.min(height - 1, regionY + searchParams.searchRadius);
        
        // Look for suitable spots
        for (let y = minY; y <= maxY; y += 4) {
          for (let x = minX; x <= maxX; x += 4) {
            // Skip water areas
            if (islandMask[y][x] < 0.7) continue;
            
            // Check biome compatibility
            const biome = biomeMap[y][x];
            if (!biome || !landmark.biomes.includes(biome.id)) continue;
            
            // Check slope if required
            if (landmark.requireFlat && this.isSlopeTooSteep(heightMap, x, y)) continue;
            
            // Check height preference
            const height = heightMap[y][x];
            const isHighPoint = this.isHighPoint(heightMap, x, y, 5);
            if (landmark.preferHigh && !isHighPoint) continue;
            
            potentialLocations.push({ x, y, score: this.scoreLandmarkLocation(heightMap, x, y, landmark) });
          }
        }
      }
      
      // Sort by score (higher is better)
      potentialLocations.sort((a, b) => b.score - a.score);
      
      // Try to place the landmark at the best location that doesn't conflict with others
      for (const loc of potentialLocations) {
        // Check minimum distance from other landmarks
        let tooClose = false;
        
        // Check distance from existing landmarks
        for (const existingLoc of locations) {
          const dx = existingLoc.x - loc.x;
          const dy = existingLoc.y - loc.y;
          const distSq = dx * dx + dy * dy;
          
          if (distSq < minDistanceHM * minDistanceHM) {
            tooClose = true;
            break;
          }
        }
        
        // Check distance from existing positions (like spawn points)
        for (const existingPos of existingPositions) {
          // Convert world position to height map coordinates
          const hmX = Math.floor((existingPos.x / worldSize + 0.5) * width);
          const hmY = Math.floor((existingPos.z / worldSize + 0.5) * height);
          
          const dx = hmX - loc.x;
          const dy = hmY - loc.y;
          const distSq = dx * dx + dy * dy;
          
          if (distSq < minDistanceHM * minDistanceHM * 2) { // Extra spacing for spawn points
            tooClose = true;
            break;
          }
        }
        
        if (!tooClose) {
          // Convert to world position
          const worldX = ((loc.x / width) - 0.5) * worldSize;
          const worldZ = ((loc.y / height) - 0.5) * worldSize;
          const worldY = heightMap[loc.y][loc.x] * 10; // Height scale is 10
          
          // Add to locations
          locations.push({
            x: loc.x,
            y: loc.y,
            worldPos: new THREE.Vector3(worldX, worldY, worldZ),
            type: landmarkType,
            landmark: landmark
          });
          
          break;
        }
      }
    }
    
    return locations;
  }
  
  /**
   * Calculate a score for a landmark location based on landmark preferences
   * @param {Array<Array<number>>} heightMap - 2D height map data
   * @param {number} x - X coordinate in height map
   * @param {number} y - Y coordinate in height map
   * @param {Object} landmark - Landmark type info
   * @returns {number} Score (higher is better)
   */
  scoreLandmarkLocation(heightMap, x, y, landmark) {
    let score = 0;
    
    // Base score
    score += 5;
    
    // If it prefers high points, score based on height
    if (landmark.preferHigh) {
      score += heightMap[y][x] * 10;
      
      // Bonus for being a local high point
      if (this.isHighPoint(heightMap, x, y, 5)) {
        score += 5;
      }
    }
    
    // If it requires flat terrain, score based on flatness
    if (landmark.requireFlat) {
      const flatness = this.calculateFlatness(heightMap, x, y, 3);
      score += flatness * 5;
    }
    
    // Random factor for variety
    score += this.seededRandom(`score-${x}-${y}-${landmark.id}`)() * 2;
    
    return score;
  }
  
  /**
   * Get search region parameters based on landmark preferences
   * @param {Object} landmark - Landmark type info
   * @param {number} width - Width of the height map
   * @param {number} height - Height of the height map
   * @returns {Object} Search region parameters
   */
  getSearchRegion(landmark, width, height) {
    const center = Math.floor(width / 2);
    const radius = Math.floor(width / 3);
    
    // Different strategies for different landmarks
    if (landmark.id === 'lighthouse') {
      // Lighthouses should be near the coast
      return {
        centerX: center,
        centerY: center,
        radiusX: radius * 0.8,
        radiusY: radius * 0.8,
        searchRadius: Math.floor(width * 0.1)
      };
    } else if (landmark.id === 'temple' || landmark.id === 'tower') {
      // Temples and towers should be in more central or elevated areas
      return {
        centerX: center,
        centerY: center,
        radiusX: radius * 0.6,
        radiusY: radius * 0.6,
        searchRadius: Math.floor(width * 0.1)
      };
    } else {
      // Other landmarks can be more distributed
      return {
        centerX: center,
        centerY: center,
        radiusX: radius * 0.7,
        radiusY: radius * 0.7,
        searchRadius: Math.floor(width * 0.15)
      };
    }
  }
  
  /**
   * Check if a location is a local high point
   * @param {Array<Array<number>>} heightMap - 2D height map data
   * @param {number} x - X coordinate in height map
   * @param {number} y - Y coordinate in height map
   * @param {number} radius - Radius to check
   * @returns {boolean} True if this is a local high point
   */
  isHighPoint(heightMap, x, y, radius) {
    const width = heightMap.length;
    const height = heightMap[0].length;
    const centerHeight = heightMap[y][x];
    
    // Check if this is higher than most points in the radius
    let higherCount = 0;
    let totalCount = 0;
    
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (dx === 0 && dy === 0) continue;
        
        const nx = x + dx;
        const ny = y + dy;
        
        // Skip out of bounds
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
        
        totalCount++;
        if (centerHeight > heightMap[ny][nx]) {
          higherCount++;
        }
      }
    }
    
    // If it's higher than 80% of surrounding points, consider it a high point
    return higherCount / totalCount > 0.8;
  }
  
  /**
   * Check if the slope at a location is too steep for building
   * @param {Array<Array<number>>} heightMap - 2D height map data
   * @param {number} x - X coordinate in height map
   * @param {number} y - Y coordinate in height map
   * @returns {boolean} True if slope is too steep
   */
  isSlopeTooSteep(heightMap, x, y) {
    const width = heightMap.length;
    const height = heightMap[0].length;
    
    // Check neighboring points
    const neighbors = [
      { dx: 1, dy: 0 },
      { dx: -1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: 0, dy: -1 }
    ];
    
    const centerHeight = heightMap[y][x];
    let maxGradient = 0;
    
    for (const neighbor of neighbors) {
      const nx = x + neighbor.dx;
      const ny = y + neighbor.dy;
      
      // Skip out of bounds
      if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
      
      const neighborHeight = heightMap[ny][nx];
      const gradient = Math.abs(centerHeight - neighborHeight);
      
      maxGradient = Math.max(maxGradient, gradient);
    }
    
    // If any gradient is above this threshold, consider it too steep
    return maxGradient > 0.02;
  }
  
  /**
   * Calculate flatness metric for a location
   * @param {Array<Array<number>>} heightMap - 2D height map data
   * @param {number} x - X coordinate in height map
   * @param {number} y - Y coordinate in height map 
   * @param {number} radius - Radius to check
   * @returns {number} Flatness score (0-1, higher is flatter)
   */
  calculateFlatness(heightMap, x, y, radius) {
    const width = heightMap.length;
    const height = heightMap[0].length;
    const centerHeight = heightMap[y][x];
    
    // Sample points in the radius
    let heightSum = 0;
    let heightSqSum = 0;
    let count = 0;
    
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const nx = x + dx;
        const ny = y + dy;
        
        // Skip out of bounds
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
        
        const pointHeight = heightMap[ny][nx];
        heightSum += pointHeight;
        heightSqSum += pointHeight * pointHeight;
        count++;
      }
    }
    
    // Calculate standard deviation
    const mean = heightSum / count;
    const variance = (heightSqSum / count) - (mean * mean);
    const stdDev = Math.sqrt(Math.max(0, variance));
    
    // Convert to flatness score (0-1)
    // Lower standard deviation means flatter terrain
    return Math.max(0, 1 - (stdDev * 100));
  }
  
  /**
   * Create a landmark at the specified position
   * @param {string} landmarkType - Type of landmark to create
   * @param {THREE.Vector3} position - World position
   * @param {THREE.Scene} scene - The Three.js scene
   * @returns {THREE.Object3D} The created landmark
   */
  createLandmark(landmarkType, position, scene) {
    const info = PointsOfInterestSystem.LANDMARK_TYPES[landmarkType];
    if (!info) {
      console.error(`Unknown landmark type: ${landmarkType}`);
      return null;
    }
    
    let landmark;
    
    // Create different structures based on type
    switch (landmarkType) {
      case 'TOWER':
        landmark = this.createTower(position, info);
        break;
      case 'RUINS':
        landmark = this.createRuins(position, info);
        break;
      case 'CABIN':
        landmark = this.createCabin(position, info);
        break;
      case 'LIGHTHOUSE':
        landmark = this.createLighthouse(position, info);
        break;
      case 'TEMPLE':
        landmark = this.createTemple(position, info);
        break;
      default:
        landmark = this.createGenericLandmark(position, info);
    }
    
    if (landmark) {
      // Apply scale
      const scaleRandom = this.seededRandom(`${this.options.seed}-scale-${landmarkType}`);
      const scale = info.scale.min + scaleRandom() * (info.scale.max - info.scale.min);
      landmark.scale.set(scale, scale, scale);
      
      // Add metadata to the main landmark group
      landmark.userData = {
        type: 'landmark',
        landmarkType: landmarkType,
        name: info.name,
        isObstacle: true, // For collision detection
        isInteractable: true // For future interaction system
      };
      
      // Add obstacle property to all child meshes for proper collision detection
      landmark.traverse(object => {
        if (object.isMesh) {
          // Set obstacle property on each mesh
          object.userData = {
            ...object.userData,
            isObstacle: true,
            type: 'landmark-part',
            parentType: landmarkType
          };
          
          // Ensure each mesh has computed bounding box for collision detection
          if (!object.geometry.boundingBox) {
            object.geometry.computeBoundingBox();
          }
        }
      });
      
      // Add to scene
      scene.add(landmark);
    }
    
    return landmark;
  }
  
  /**
   * Add a simplified collision mesh to a landmark
   * @param {THREE.Object3D} landmark - The landmark to add collision to
   * @param {Object} dimensions - Width, height, depth
   * @param {THREE.Vector3} position - Position offset
   */
  addCollisionMesh(landmark, dimensions, position = new THREE.Vector3(0, 0, 0)) {
    // Create a simplified collision box
    const collisionGeometry = new THREE.BoxGeometry(
      dimensions.width,
      dimensions.height,
      dimensions.depth
    );
    
    // Use an invisible material for the collision mesh
    const collisionMaterial = new THREE.MeshBasicMaterial({
      visible: false,
      transparent: true,
      opacity: 0
    });
    
    const collisionMesh = new THREE.Mesh(collisionGeometry, collisionMaterial);
    collisionMesh.position.copy(position);
    
    // Mark as collision-only object
    collisionMesh.userData = {
      isObstacle: true,
      isCollider: true,
      type: 'landmark-collider'
    };
    
    // Ensure it has a bounding box
    collisionGeometry.computeBoundingBox();
    
    // Add to landmark
    landmark.add(collisionMesh);
    
    return collisionMesh;
  }

  /**
   * Create a tower landmark
   * @param {THREE.Vector3} position - World position
   * @param {Object} info - Landmark info
   * @returns {THREE.Group} Tower group
   */
  createTower(position, info) {
    const tower = new THREE.Group();
    
    // Base platform
    const baseGeometry = new THREE.BoxGeometry(4, 1, 4);
    const baseMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    const base = new THREE.Mesh(baseGeometry, baseMaterial);
    base.position.y = 0.5;
    tower.add(base);
    
    // Stone foundation
    const foundationGeometry = new THREE.BoxGeometry(3.5, 0.8, 3.5);
    const stoneMaterial = new THREE.MeshStandardMaterial({ color: 0x808080 });
    const foundation = new THREE.Mesh(foundationGeometry, stoneMaterial);
    foundation.position.y = 1.4;
    tower.add(foundation);
    
    // Tower body with segmented architecture
    const segments = 3;
    const segmentHeight = 2.2;
    const totalHeight = segments * segmentHeight;
    
    for (let i = 0; i < segments; i++) {
      // Each segment gets slightly narrower
      const width = 2.5 - (i * 0.2);
      const segGeometry = new THREE.BoxGeometry(width, segmentHeight, width);
      const segMaterial = i === segments - 1 
        ? new THREE.MeshStandardMaterial({ color: 0x8B4513 }) // Top segment
        : new THREE.MeshStandardMaterial({ color: 0x8B4513 });
      
      const segment = new THREE.Mesh(segGeometry, segMaterial);
      segment.position.y = 2.5 + i * segmentHeight + segmentHeight/2;
      tower.add(segment);
    }
    
    // Tower windows (one on each face for the middle segment)
    const windowMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x4F4F4F,
      transparent: true,
      opacity: 0.7
    });
    
    const directions = [
      [0, 0, 1], [1, 0, 0], [0, 0, -1], [-1, 0, 0]
    ];
    
    directions.forEach(dir => {
      const windowGeometry = new THREE.BoxGeometry(0.4, 0.8, 0.1);
      const window = new THREE.Mesh(windowGeometry, windowMaterial);
      
      // Position the window on the face of the middle segment
      window.position.set(
        dir[0] * 1.2, 
        2.5 + segmentHeight + segmentHeight/2, 
        dir[2] * 1.2
      );
      
      // Rotate the window to face outward
      if (dir[0] !== 0) {
        window.rotateY(Math.PI / 2);
      }
      
      tower.add(window);
    });
    
    // Tower top with battlements
    const topGeometry = new THREE.BoxGeometry(2.8, 1.2, 2.8);
    const topMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    const top = new THREE.Mesh(topGeometry, topMaterial);
    top.position.y = 2.5 + totalHeight + 0.6;
    tower.add(top);
    
    // Add battlements/crenellations around the top
    const battlementCount = 8;
    for (let i = 0; i < battlementCount; i++) {
      const angle = (i / battlementCount) * Math.PI * 2;
      const radius = 1.4;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      
      const merlon = new THREE.BoxGeometry(0.5, 0.8, 0.5);
      const merlonMesh = new THREE.Mesh(merlon, topMaterial);
      merlonMesh.position.set(x, 2.5 + totalHeight + 1.7, z);
      tower.add(merlonMesh);
    }
    
    // Add a flag
    const flagpoleGeometry = new THREE.CylinderGeometry(0.05, 0.05, 1.8, 8);
    const flagpoleMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
    const flagpole = new THREE.Mesh(flagpoleGeometry, flagpoleMaterial);
    flagpole.position.set(0, 2.5 + totalHeight + 2.6, 0);
    tower.add(flagpole);
    
    // Create flag
    const flagGeometry = new THREE.PlaneGeometry(0.8, 0.5);
    const flagMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xFF0000, 
      side: THREE.DoubleSide 
    });
    const flag = new THREE.Mesh(flagGeometry, flagMaterial);
    flag.position.set(0.4, 0.7, 0);
    flag.rotateY(Math.PI / 2);
    flagpole.add(flag);
    
    // Add a simple collision box for the entire tower
    this.addCollisionMesh(tower, { width: 4, height: totalHeight + 4, depth: 4 }, 
      new THREE.Vector3(0, (totalHeight + 4) / 2, 0));
    
    // Set position
    tower.position.copy(position);
    
    // Enable shadows
    tower.traverse(object => {
      if (object.isMesh) {
        object.castShadow = true;
        object.receiveShadow = true;
      }
    });
    
    return tower;
  }
  
  /**
   * Create ruins landmark
   * @param {THREE.Vector3} position - World position
   * @param {Object} info - Landmark info
   * @returns {THREE.Group} Ruins group
   */
  createRuins(position, info) {
    const ruins = new THREE.Group();
    
    // Main platform with weathered/damaged appearance
    const platformGeometry = new THREE.BoxGeometry(8, 0.6, 8);
    const platformMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xA0A0A0,
      roughness: 0.9,
      metalness: 0.1
    });
    const platform = new THREE.Mesh(platformGeometry, platformMaterial);
    platform.position.y = 0.3;
    ruins.add(platform);
    
    // Create a perimeter of broken walls
    for (let i = 0; i < 4; i++) {
      // Position along perimeter
      const angle = (i / 4) * Math.PI * 2;
      const radius = 3;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      
      // Each wall section has random damage/height
      const height = 0.8 + Math.random() * 1.5;
      const width = 1.5 + Math.random() * 2;
      
      const wallGeometry = new THREE.BoxGeometry(width, height, 0.5);
      const wallMaterial = new THREE.MeshStandardMaterial({ 
        color: 0x909090,
        roughness: 0.8
      });
      const wall = new THREE.Mesh(wallGeometry, wallMaterial);
      
      // Position and rotate to face center
      wall.position.set(x, height / 2 + 0.6, z);
      wall.lookAt(new THREE.Vector3(0, wall.position.y, 0));
      
      // Add some random rotation damage
      wall.rotation.z += (Math.random() - 0.5) * 0.2;
      wall.rotation.x += (Math.random() - 0.5) * 0.1;
      
      ruins.add(wall);
      
      // Add wall collision
      this.addCollisionMesh(ruins, { width: width, height: height, depth: 0.5 }, wall.position.clone());
    }
    
    // Create broken columns
    const columnCount = 5 + Math.floor(Math.random() * 3);
    for (let i = 0; i < columnCount; i++) {
      // Random position inside the ruins
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * 2.5;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      
      // Random height for broken column
      const height = 0.5 + Math.random() * 3;
      const isToppled = Math.random() > 0.6;
      
      if (isToppled) {
        // Create a fallen column
        const columnGeometry = new THREE.CylinderGeometry(0.3, 0.3, height, 8);
        const columnMaterial = new THREE.MeshStandardMaterial({ 
          color: 0xB8B8B8,
          roughness: 0.7
        });
        const column = new THREE.Mesh(columnGeometry, columnMaterial);
        
        // Position on ground, rotated to lie down
        column.position.set(x, height/2 * 0.2, z);
        column.rotation.x = Math.PI / 2;
        column.rotation.z = Math.random() * Math.PI;
        
        ruins.add(column);
        
        // Add collision for the fallen column
        this.addCollisionMesh(ruins, { width: 0.6, height: 0.6, depth: height }, 
          new THREE.Vector3(x, 0.3, z));
      } else {
        // Create a standing broken column
        const columnGeometry = new THREE.CylinderGeometry(0.3, 0.35, height, 8);
        const columnMaterial = new THREE.MeshStandardMaterial({ 
          color: 0xB0B0B0,
          roughness: 0.8
        });
        const column = new THREE.Mesh(columnGeometry, columnMaterial);
        
        column.position.set(x, height / 2 + 0.6, z);
        
        // Add random slight tilt
        column.rotation.x = (Math.random() - 0.5) * 0.2;
        column.rotation.z = (Math.random() - 0.5) * 0.2;
        
        ruins.add(column);
        
        // Add column capital (top decoration)
        if (Math.random() > 0.5) {
          const capitalGeometry = new THREE.BoxGeometry(0.7, 0.2, 0.7);
          const capitalMaterial = new THREE.MeshStandardMaterial({ color: 0xB8B8B8 });
          const capital = new THREE.Mesh(capitalGeometry, capitalMaterial);
          capital.position.y = height / 2 + 0.1;
          column.add(capital);
        }
        
        // Add individual column collision
        this.addCollisionMesh(ruins, { width: 0.7, height: height, depth: 0.7 }, column.position);
      }
    }
    
    // Add some debris/rubble
    for (let i = 0; i < 12; i++) {
      const debrisSize = 0.1 + Math.random() * 0.4;
      const debrisGeometry = new THREE.DodecahedronGeometry(debrisSize, 0);
      const debrisMaterial = new THREE.MeshStandardMaterial({ 
        color: 0xA0A0A0,
        roughness: 1.0
      });
      const debris = new THREE.Mesh(debrisGeometry, debrisMaterial);
      
      // Random position on the platform
      const angle = Math.random() * Math.PI * 2;
      const radius = Math.random() * 3.5;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      
      debris.position.set(x, debrisSize + 0.6, z);
      debris.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );
      
      ruins.add(debris);
    }
    
    // Central altar or statue base
    const altarGeometry = new THREE.BoxGeometry(1.5, 1, 1.5);
    const altarMaterial = new THREE.MeshStandardMaterial({ color: 0xB0B0B0 });
    const altar = new THREE.Mesh(altarGeometry, altarMaterial);
    altar.position.set(0, 1.1, 0);
    ruins.add(altar);
    
    // Add broken statue
    const torsoGeometry = new THREE.CylinderGeometry(0.3, 0.4, 0.8, 8);
    const torsoMaterial = new THREE.MeshStandardMaterial({ color: 0xE0E0E0 });
    const torso = new THREE.Mesh(torsoGeometry, torsoMaterial);
    torso.position.set(0, 0.7, 0);
    altar.add(torso);
    
    // Add platform collision
    this.addCollisionMesh(ruins, { width: 8, height: 0.6, depth: 8 }, new THREE.Vector3(0, 0.3, 0));
    
    // Add altar collision
    this.addCollisionMesh(ruins, { width: 1.5, height: 1, depth: 1.5 }, new THREE.Vector3(0, 1.1, 0));
    
    // Set position
    ruins.position.copy(position);
    
    // Enable shadows
    ruins.traverse(object => {
      if (object.isMesh) {
        object.castShadow = true;
        object.receiveShadow = true;
      }
    });
    
    return ruins;
  }
  
  /**
   * Create cabin landmark
   * @param {THREE.Vector3} position - World position
   * @param {Object} info - Landmark info
   * @returns {THREE.Group} Cabin group
   */
  createCabin(position, info) {
    const cabin = new THREE.Group();
    
    // Raised wooden platform/foundation
    const baseGeometry = new THREE.BoxGeometry(5, 0.4, 5);
    const baseMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x8B4513,
      roughness: 0.9
    });
    const base = new THREE.Mesh(baseGeometry, baseMaterial);
    base.position.y = 0.2;
    cabin.add(base);
    
    // Create support logs under platform
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2 + (Math.PI / 4);
      const radius = 1.8;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      
      const pillarGeometry = new THREE.CylinderGeometry(0.2, 0.2, 0.4, 6);
      const pillarMaterial = new THREE.MeshStandardMaterial({ color: 0x5D4037 });
      const pillar = new THREE.Mesh(pillarGeometry, pillarMaterial);
      pillar.position.set(x, 0, z);
      cabin.add(pillar);
    }
    
    // Main cabin structure
    const wallGeometry = new THREE.BoxGeometry(4, 2.4, 4);
    const wallMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x8B4513,
      map: this.createWoodTexture()
    });
    const walls = new THREE.Mesh(wallGeometry, wallMaterial);
    walls.position.y = 1.6;
    cabin.add(walls);
    
    // Add log details to wall corners
    for (let x = -1; x <= 1; x += 2) {
      for (let z = -1; z <= 1; z += 2) {
        const logGeometry = new THREE.CylinderGeometry(0.15, 0.15, 2.4, 6);
        const logMaterial = new THREE.MeshStandardMaterial({ color: 0x5D4037 });
        const log = new THREE.Mesh(logGeometry, logMaterial);
        log.position.set(x * 2, 1.6, z * 2);
        cabin.add(log);
      }
    }
    
    // Pitched roof
    const roofGroup = new THREE.Group();
    roofGroup.position.y = 2.8;
    cabin.add(roofGroup);
    
    // Roof sides
    const roofHeight = 1.8;
    const roofOverhang = 0.5;
    const roofWidth = 4 + roofOverhang * 2;
    
    // Front and back roof panels
    for (let z = -1; z <= 1; z += 2) {
      const roofGeometry = new THREE.BufferGeometry();
      const vertices = new Float32Array([
        -roofWidth/2, 0, z * (4/2 + roofOverhang), // bottom left
        roofWidth/2, 0, z * (4/2 + roofOverhang),  // bottom right
        0, roofHeight, z * (4/2)                   // top middle
      ]);
      roofGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
      roofGeometry.computeVertexNormals();
      
      const roofMaterial = new THREE.MeshStandardMaterial({ 
        color: 0x5D4037,
        side: THREE.DoubleSide
      });
      const roofPanel = new THREE.Mesh(roofGeometry, roofMaterial);
      roofGroup.add(roofPanel);
    }
    
    // Side roof panels - left and right
    for (let x = -1; x <= 1; x += 2) {
      const sideRoofGeometry = new THREE.BufferGeometry();
      // Create a triangle for each side of the roof
      const halfWidth = 4/2;
      const vertices = new Float32Array([
        x * (halfWidth + roofOverhang), 0, -halfWidth - roofOverhang, // front bottom
        x * (halfWidth + roofOverhang), 0, halfWidth + roofOverhang,  // back bottom
        x * halfWidth, roofHeight, 0                                  // top middle
      ]);
      sideRoofGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
      sideRoofGeometry.computeVertexNormals();
      
      const sideRoofMaterial = new THREE.MeshStandardMaterial({ 
        color: 0x5D4037,
        side: THREE.DoubleSide
      });
      const sideRoofPanel = new THREE.Mesh(sideRoofGeometry, sideRoofMaterial);
      roofGroup.add(sideRoofPanel);
    }
    
    // Door
    const doorGeometry = new THREE.PlaneGeometry(0.9, 1.8);
    const doorMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x4D2600,
      side: THREE.DoubleSide
    });
    const door = new THREE.Mesh(doorGeometry, doorMaterial);
    door.position.set(0, 1.3, 2.01);
    cabin.add(door);
    
    // Door frame
    const frameGeometry = new THREE.BoxGeometry(1.1, 1.9, 0.1);
    const frameMaterial = new THREE.MeshStandardMaterial({ color: 0x3E2723 });
    const doorFrame = new THREE.Mesh(frameGeometry, frameMaterial);
    doorFrame.position.set(0, 1.35, 2.05);
    cabin.add(doorFrame);
    
    // Add windows
    for (let x = -1; x <= 1; x += 2) {
      const windowGeometry = new THREE.PlaneGeometry(0.8, 0.8);
      const windowMaterial = new THREE.MeshStandardMaterial({ 
        color: 0xADD8E6,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide
      });
      const window = new THREE.Mesh(windowGeometry, windowMaterial);
      window.position.set(x * 1.5, 1.8, 2.01);
      cabin.add(window);
      
      // Window frame
      const windowFrameGeometry = new THREE.BoxGeometry(1, 1, 0.1);
      const windowFrameMaterial = new THREE.MeshStandardMaterial({ color: 0x3E2723 });
      const windowFrame = new THREE.Mesh(windowFrameGeometry, windowFrameMaterial);
      windowFrame.position.set(x * 1.5, 1.8, 2.05);
      cabin.add(windowFrame);
      
      // Window cross
      const crossVGeometry = new THREE.BoxGeometry(0.05, 0.8, 0.05);
      const crossHGeometry = new THREE.BoxGeometry(0.8, 0.05, 0.05);
      const crossMaterial = new THREE.MeshStandardMaterial({ color: 0x3E2723 });
      
      const crossV = new THREE.Mesh(crossVGeometry, crossMaterial);
      crossV.position.set(x * 1.5, 1.8, 2.08);
      cabin.add(crossV);
      
      const crossH = new THREE.Mesh(crossHGeometry, crossMaterial);
      crossH.position.set(x * 1.5, 1.8, 2.08);
      cabin.add(crossH);
    }
    
    // Side windows
    for (let side = -1; side <= 1; side += 2) {
      const sideWindowGeometry = new THREE.PlaneGeometry(0.8, 0.8);
      const sideWindowMaterial = new THREE.MeshStandardMaterial({ 
        color: 0xADD8E6,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide
      });
      const sideWindow = new THREE.Mesh(sideWindowGeometry, sideWindowMaterial);
      sideWindow.position.set(side * 2.01, 1.8, 0);
      sideWindow.rotation.y = Math.PI / 2;
      cabin.add(sideWindow);
      
      // Window frame
      const sideFrameGeometry = new THREE.BoxGeometry(0.1, 1, 1);
      const sideFrameMaterial = new THREE.MeshStandardMaterial({ color: 0x3E2723 });
      const sideFrame = new THREE.Mesh(sideFrameGeometry, sideFrameMaterial);
      sideFrame.position.set(side * 2.05, 1.8, 0);
      cabin.add(sideFrame);
    }
    
    // Add chimney
    const chimneyGeometry = new THREE.BoxGeometry(0.6, 2, 0.6);
    const chimneyMaterial = new THREE.MeshStandardMaterial({ color: 0x8D6E63 });
    const chimney = new THREE.Mesh(chimneyGeometry, chimneyMaterial);
    chimney.position.set(-1.2, 3.4, -1);
    cabin.add(chimney);
    
    // Small log pile next to cabin
    const logPile = new THREE.Group();
    logPile.position.set(-2.2, 0.4, 1.5);
    cabin.add(logPile);
    
    for (let i = 0; i < 5; i++) {
      const logGeometry = new THREE.CylinderGeometry(0.15, 0.15, 1.2, 6);
      const logMaterial = new THREE.MeshStandardMaterial({ color: 0x5D4037 });
      const log = new THREE.Mesh(logGeometry, logMaterial);
      
      // Stack logs alternating
      const layerIndex = Math.floor(i / 2);
      const isHorizontal = i % 2 === 0;
      
      if (isHorizontal) {
        log.position.y = layerIndex * 0.3;
        log.rotation.z = Math.PI / 2;
      } else {
        log.position.y = layerIndex * 0.3;
        log.position.x = 0.3;
        log.rotation.x = Math.PI / 2;
      }
      
      logPile.add(log);
    }
    
    // Add a simple collision box for the entire cabin
    this.addCollisionMesh(cabin, { width: 5, height: 5, depth: 5 }, new THREE.Vector3(0, 2.5, 0));
    
    // Set position
    cabin.position.copy(position);
    
    // Enable shadows
    cabin.traverse(object => {
      if (object.isMesh) {
        object.castShadow = true;
        object.receiveShadow = true;
      }
    });
    
    return cabin;
  }
  
  /**
   * Create a simple procedural wood texture
   * @returns {THREE.Texture} The generated texture
   */
  createWoodTexture() {
    // Create a canvas element
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const context = canvas.getContext('2d');
    
    // Fill with base wood color
    context.fillStyle = '#8B4513';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add wood grain
    for (let i = 0; i < 12; i++) {
      const y = Math.random() * canvas.height;
      const width = 2 + Math.random() * 4;
      const colorAdjust = Math.random() * 30 - 15;
      
      const colorValue = 139 + colorAdjust; // Base on 8B4513 (139, 69, 19)
      context.fillStyle = `rgb(${colorValue}, ${colorAdjust + 69}, ${colorAdjust + 19})`;
      context.fillRect(0, y, canvas.width, width);
    }
    
    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(1, 1);
    
    return texture;
  }
  
  /**
   * Create lighthouse landmark
   * @param {THREE.Vector3} position - World position
   * @param {Object} info - Landmark info
   * @returns {THREE.Group} Lighthouse group
   */
  createLighthouse(position, info) {
    const lighthouse = new THREE.Group();
    
    // Rocky base/small island
    const baseGeometry = new THREE.CylinderGeometry(3, 3.5, 1.2, 16, 1, false);
    const baseMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x808080,
      roughness: 0.9
    });
    const base = new THREE.Mesh(baseGeometry, baseMaterial);
    base.position.y = 0.6;
    
    // Add rocky details to base
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const radius = 2.8 + Math.random() * 0.5;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      
      const rockSize = 0.3 + Math.random() * 0.4;
      const rockGeometry = new THREE.DodecahedronGeometry(rockSize, 0);
      const rockMaterial = new THREE.MeshStandardMaterial({ 
        color: 0x707070,
        roughness: 1.0
      });
      const rock = new THREE.Mesh(rockGeometry, rockMaterial);
      rock.position.set(x, rockSize * 0.3, z);
      rock.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );
      base.add(rock);
    }
    
    lighthouse.add(base);
    
    // Add stone path/steps leading to lighthouse
    const pathLength = 6;
    const pathStart = -3;
    for (let i = 0; i < 5; i++) {
      const stepGeometry = new THREE.BoxGeometry(1.2, 0.2, 0.6);
      const stepMaterial = new THREE.MeshStandardMaterial({ color: 0xAAAAAA });
      const step = new THREE.Mesh(stepGeometry, stepMaterial);
      
      // Position steps in a line
      const progress = i / 4; // 0 to 1
      step.position.set(
        0, 
        0.1 + progress * 0.5, // Steps rise up slightly
        pathStart + progress * pathLength
      );
      lighthouse.add(step);
    }
    
    // Main lighthouse tower - tapered cylinder
    const segments = 5;
    const totalHeight = 9;
    const segmentHeight = totalHeight / segments;
    
    for (let i = 0; i < segments; i++) {
      // Create segments that get smaller as they go up
      const radiusTop = 1.6 - (i * 0.2);
      const radiusBottom = 1.8 - (i * 0.2);
      
      // Alternate colors for lighthouse stripes
      const color = i % 2 === 0 ? 0xFFFFFF : 0xFF0000;
      
      const segmentGeometry = new THREE.CylinderGeometry(
        radiusTop, radiusBottom, segmentHeight, 16
      );
      const segmentMaterial = new THREE.MeshStandardMaterial({ 
        color: color,
        roughness: 0.7
      });
      const segment = new THREE.Mesh(segmentGeometry, segmentMaterial);
      segment.position.y = 1.8 + i * segmentHeight + segmentHeight/2;
      lighthouse.add(segment);
    }
    
    // Observation deck/gallery at top
    const deckGeometry = new THREE.CylinderGeometry(1.8, 1.8, 0.4, 16);
    const deckMaterial = new THREE.MeshStandardMaterial({ color: 0xCCCCCC });
    const deck = new THREE.Mesh(deckGeometry, deckMaterial);
    deck.position.y = 1.8 + totalHeight + 0.2;
    lighthouse.add(deck);
    
    // Railing around observation deck
    const railingRadius = 1.8;
    const railingHeight = 0.8;
    
    // Create railing posts
    const postCount = 16;
    for (let i = 0; i < postCount; i++) {
      const angle = (i / postCount) * Math.PI * 2;
      const x = Math.cos(angle) * railingRadius;
      const z = Math.sin(angle) * railingRadius;
      
      const postGeometry = new THREE.CylinderGeometry(0.05, 0.05, railingHeight, 4);
      const postMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
      const post = new THREE.Mesh(postGeometry, postMaterial);
      post.position.set(x, 1.8 + totalHeight + 0.2 + railingHeight/2, z);
      lighthouse.add(post);
    }
    
    // Horizontal railing
    const railingGeometry = new THREE.TorusGeometry(railingRadius, 0.04, 8, postCount);
    const railingMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
    const railingTop = new THREE.Mesh(railingGeometry, railingMaterial);
    railingTop.position.y = 1.8 + totalHeight + 0.2 + railingHeight;
    railingTop.rotation.x = Math.PI / 2;
    lighthouse.add(railingTop);
    
    // Lantern room (glass enclosure for light)
    const lanternGeometry = new THREE.CylinderGeometry(1.2, 1.4, 1.5, 16);
    const lanternMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xADD8E6,
      transparent: true,
      opacity: 0.6
    });
    const lantern = new THREE.Mesh(lanternGeometry, lanternMaterial);
    lantern.position.y = 1.8 + totalHeight + 1.5;
    lighthouse.add(lantern);
    
    // Lantern vertical supports
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const x = Math.cos(angle) * 1.3;
      const z = Math.sin(angle) * 1.3;
      
      const supportGeometry = new THREE.BoxGeometry(0.1, 1.5, 0.1);
      const supportMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
      const support = new THREE.Mesh(supportGeometry, supportMaterial);
      support.position.set(x, 1.8 + totalHeight + 1.5, z);
      lighthouse.add(support);
    }
    
    // Light dome/top
    const domeGeometry = new THREE.SphereGeometry(0.9, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2);
    const domeMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x333333
    });
    const dome = new THREE.Mesh(domeGeometry, domeMaterial);
    dome.position.y = 1.8 + totalHeight + 2.3;
    lighthouse.add(dome);
    
    // Light source - emissive material
    const lightGeometry = new THREE.SphereGeometry(0.6, 12, 12);
    const lightMaterial = new THREE.MeshStandardMaterial({ 
      color: 0xFFFF00,
      emissive: 0xFFFF00,
      emissiveIntensity: 1.0
    });
    const lightSource = new THREE.Mesh(lightGeometry, lightMaterial);
    lightSource.position.y = 1.8 + totalHeight + 1.5;
    lighthouse.add(lightSource);
    
    // Create an actual light source
    const pointLight = new THREE.PointLight(0xFFFF00, 1, 20);
    pointLight.position.set(0, 1.8 + totalHeight + 1.5, 0);
    lighthouse.add(pointLight);
    
    // Doorway at base
    const doorGeometry = new THREE.PlaneGeometry(0.8, 1.6);
    const doorMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x8B4513, 
      side: THREE.DoubleSide
    });
    const door = new THREE.Mesh(doorGeometry, doorMaterial);
    door.position.set(0, 2, -1.61);
    lighthouse.add(door);
    
    // Door frame
    const frameGeometry = new THREE.BoxGeometry(1, 1.8, 0.1);
    const frameMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
    const doorFrame = new THREE.Mesh(frameGeometry, frameMaterial);
    doorFrame.position.set(0, 2.1, -1.65);
    lighthouse.add(doorFrame);
    
    // Small windows spiraling up the tower
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2;
      const height = 3 + i * 2;
      
      const windowGeometry = new THREE.PlaneGeometry(0.5, 0.7);
      const windowMaterial = new THREE.MeshStandardMaterial({ 
        color: 0xADD8E6,
        transparent: true,
        opacity: 0.7,
        side: THREE.DoubleSide
      });
      const window = new THREE.Mesh(windowGeometry, windowMaterial);
      
      const x = Math.cos(angle) * 1.59;
      const z = Math.sin(angle) * 1.59;
      window.position.set(x, height, z);
      window.lookAt(new THREE.Vector3(0, height, 0));
      lighthouse.add(window);
    }
    
    // Add a collision cylinder for the lighthouse
    this.addCollisionMesh(lighthouse, { width: 4, height: totalHeight + 5, depth: 4 }, 
      new THREE.Vector3(0, (totalHeight + 5) / 2, 0));
    
    // Set position
    lighthouse.position.copy(position);
    
    // Enable shadows
    lighthouse.traverse(object => {
      if (object.isMesh) {
        object.castShadow = true;
        object.receiveShadow = true;
      }
    });
    
    return lighthouse;
  }
  
  /**
   * Create temple landmark
   * @param {THREE.Vector3} position - World position
   * @param {Object} info - Landmark info
   * @returns {THREE.Group} Temple group
   */
  createTemple(position, info) {
    const temple = new THREE.Group();
    
    // Platform stairs
    for (let i = 0; i < 3; i++) {
      const size = 8 - i;
      const platformGeometry = new THREE.BoxGeometry(size, 0.5, size);
      const platformMaterial = new THREE.MeshStandardMaterial({ color: 0xD2B48C });
      const platform = new THREE.Mesh(platformGeometry, platformMaterial);
      platform.position.y = i * 0.5 + 0.25;
      temple.add(platform);
    }
    
    // Main structure
    const mainGeometry = new THREE.BoxGeometry(4, 2, 4);
    const mainMaterial = new THREE.MeshStandardMaterial({ color: 0xD2B48C });
    const main = new THREE.Mesh(mainGeometry, mainMaterial);
    main.position.y = 2.5;
    temple.add(main);
    
    // Roof
    const roofGeometry = new THREE.BoxGeometry(4.5, 1, 4.5);
    const roofMaterial = new THREE.MeshStandardMaterial({ color: 0xD2B48C });
    const roof = new THREE.Mesh(roofGeometry, roofMaterial);
    roof.position.y = 4;
    temple.add(roof);
    
    // Pillars
    const pillarPositions = [
      { x: 1.5, z: 1.5 },
      { x: -1.5, z: 1.5 },
      { x: 1.5, z: -1.5 },
      { x: -1.5, z: -1.5 }
    ];
    
    pillarPositions.forEach(pos => {
      const pillarGeometry = new THREE.CylinderGeometry(0.3, 0.3, 2, 8);
      const pillarMaterial = new THREE.MeshStandardMaterial({ color: 0xD2B48C });
      const pillar = new THREE.Mesh(pillarGeometry, pillarMaterial);
      pillar.position.set(pos.x, 2.5, pos.z);
      temple.add(pillar);
      
      // Add pillar collision
      this.addCollisionMesh(temple, { width: 0.6, height: 2, depth: 0.6 }, pillar.position);
    });
    
    // Add main structure collision
    this.addCollisionMesh(temple, { width: 4.5, height: 4, depth: 4.5 }, new THREE.Vector3(0, 2.5, 0));
    
    // Add base platform collision
    this.addCollisionMesh(temple, { width: 8, height: 1.5, depth: 8 }, new THREE.Vector3(0, 0.75, 0));
    
    // Set position
    temple.position.copy(position);
    
    // Enable shadows
    temple.traverse(object => {
      if (object.isMesh) {
        object.castShadow = true;
        object.receiveShadow = true;
      }
    });
    
    return temple;
  }
  
  /**
   * Create a generic landmark for fallback
   * @param {THREE.Vector3} position - World position
   * @param {Object} info - Landmark info
   * @returns {THREE.Mesh} Generic landmark
   */
  createGenericLandmark(position, info) {
    const geometry = new THREE.BoxGeometry(
      info.dimensions.width,
      info.dimensions.height,
      info.dimensions.depth
    );
    const material = new THREE.MeshStandardMaterial({ color: info.color });
    const landmark = new THREE.Mesh(geometry, material);
    
    landmark.position.copy(position);
    landmark.position.y += info.dimensions.height / 2;
    
    // Enable shadows
    landmark.castShadow = true;
    landmark.receiveShadow = true;
    
    return landmark;
  }
  
  /**
   * Shuffle an array using seeded random
   * @param {Array} array - Array to shuffle
   * @returns {Array} Shuffled array
   */
  shuffleArray(array) {
    const newArray = [...array];
    const random = this.seededRandom(this.options.seed + '-shuffle');
    
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    
    return newArray;
  }
  
  /**
   * Create a seeded random function
   * @param {string|number} seed - Seed for random number generation
   * @returns {Function} Seeded random function
   */
  seededRandom(seed) {
    // Simple xorshift-based random number generator
    let s = this.hashSeed(seed);
    
    return function() {
      s ^= s << 13;
      s ^= s >> 17;
      s ^= s << 5;
      return ((s < 0 ? ~s + 1 : s) % 1000) / 1000;
    };
  }
  
  /**
   * Hash a seed string or number to a numeric value
   * @param {string|number} seed - Seed to hash
   * @returns {number} Numeric hash
   */
  hashSeed(seed) {
    if (typeof seed === 'number') return seed;
    
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    return hash;
  }
  
  /**
   * Clean up resources when no longer needed
   */
  dispose() {
    // Dispose of any created geometries, materials, textures, etc.
    this.landmarks.forEach(landmark => {
      landmark.traverse(object => {
        if (object.isMesh) {
          if (object.geometry) object.geometry.dispose();
          
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(material => this.disposeMaterial(material));
            } else {
              this.disposeMaterial(object.material);
            }
          }
        }
      });
    });
    
    // Clear arrays
    this.landmarks = [];
    this.debugObjects = [];
  }
  
  /**
   * Helper to dispose of a material and its textures
   * @param {THREE.Material} material - Material to dispose
   */
  disposeMaterial(material) {
    if (!material) return;
    
    // Dispose textures
    if (material.map) material.map.dispose();
    if (material.lightMap) material.lightMap.dispose();
    if (material.bumpMap) material.bumpMap.dispose();
    if (material.normalMap) material.normalMap.dispose();
    if (material.specularMap) material.specularMap.dispose();
    if (material.envMap) material.envMap.dispose();
    
    // Dispose material
    material.dispose();
  }
}

export default PointsOfInterestSystem; 